import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ModalFunctionComponent } from '../../molecules/modal-function/modal-function.component';
import { ModalFunctionType } from '../../../shared/enums/modal-function-type.enums';
import { ImageComponent } from '../../atoms/image/image.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { LinkComponent } from '../../atoms/link/link.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { SafeResourceUrl } from '@angular/platform-browser';

import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import { MediaType } from '../../../shared/enums/media-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { Router } from '@angular/router';
import { ActionType } from '../../../shared/enums/action-type.enums';
import { FontAwesomeService } from '../../../services/fontawesome.service';
import {
  CaasComponents,
  CaasIcon,
  CaasImageLink,
  CaasLink,
  CaasLinkOptional,
} from '../../../shared/interfaces/caas/caas.interface';
import { Video } from '../../../shared/interfaces/video.interface';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { PrefooterLoaderComponent } from './prefooter-loader/prefooter-loader.component';

@Component({
  selector: 'hdi-prefooter',
  standalone: true,
  imports: [
    CommonModule,
    ModalFunctionComponent,
    ImageComponent,
    ButtonComponent,
    LinkComponent,
    IconComponent,
    CapitalizePipe,
    PrefooterLoaderComponent,
  ],
  templateUrl: './prefooter.component.html',
  styleUrls: ['./prefooter.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class PrefooterComponent implements OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() imageSrc: string = '';
  @Input() imageAlt: string = '';
  @Input() imageTitle: string = '';
  @Input() videoSrc: SafeResourceUrl = '';
  @Input() videoId: string = '';
  @Input() videoTitle: string = '';
  @Input() thumbnailSrc: string = '';
  @Input() orientation: 'forward' | 'reverse' = 'forward';
  @Input() showPlayButton: boolean = false;
  @Input() headline: string = '';
  @Input() staticImage: boolean = false;
  @Input() imageBackground?: Colors;
  @Input() innerHTMLText: string = '';
  @Input() clickableCategory: ActionType = ActionType.NONE;
  @Input() clickableType: ButtonType | LinkType = ButtonType.PRIMARY;
  @Input() clickableText: string = '';
  @Input() isActive: boolean = true;
  @Input() optionalLinkText: string = '';
  @Input() clickableVideo?: Video;
  @Input() optionalLinkVideo?: Video;
  @Input() clickableIcon: string | undefined;
  @Input() clickableHref: string = '';
  @Input() optionalLinkHref: string = '';
  @Input() clickableTarget: string = '_blank';
  @Input() clickableTitle: string = '';
  @Input() optionalLinkTitle: string = '';
  @Input() optionalLinkType: LinkType = LinkType.PRIMARY_ARROW;

  @Input() badgeSrc: string = '';
  @Input() showBadge: boolean = true;
  @Input() badgeContent: MediaType = MediaType.IMAGE;
  @Input() badgeIcon: string = 'briefcase';
  @Input() clipboardTitle: string = '';
  @Input() buttonsFlex: 'row' | 'column' = 'row';
  @Input() hrefType: HrefType = HrefType.EXTERNAL;
  @Input() optionalHrefType: HrefType = HrefType.EXTERNAL;
  @Input() sectionBg?: 'white' | 'grey';
  @ViewChild('prefooter') prefooter: ElementRef = {} as ElementRef;
  ModalFunctionType = ModalFunctionType;
  MediaType = MediaType;
  HrefType = HrefType;

  Sizes = Sizes;
  Colors = Colors;
  tooltip: Tooltip = {
    id: '',
    description: '',
  };

  public isLoading = true;

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly fontAwesomeService: FontAwesomeService,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly cdr: ChangeDetectorRef,
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(): void {
    if (this.caasData) {
      this.setCaasContent(this.caasData);
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
      this.cdr.detectChanges();
    }
  }

  get buttonType(): ButtonType {
    return this.clickableType as ButtonType;
  }

  get linkType(): LinkType {
    return this.clickableType as LinkType;
  }

  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  handleClick(event: Event) {
    const linkType =
      this.caasData?.formData?.st_link?.value?.formData?.lt_link_type?.value
        ?.identifier;
    if (linkType === 'video') {
      if (this.clickableVideo) {
        const modalEvent = new CustomEvent('openVideoModal', {
          detail: this.clickableVideo,
        });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(modalEvent);
          event.preventDefault();
        }
      }
    } else {
      this.navigateTo(event);
    }
  }

  navigateTo(event: Event) {
    event.preventDefault();
    switch (this.hrefType) {
      case HrefType.EXTERNAL:
        if (typeof window !== 'undefined') {
          window.open(this.clickableHref, this.clickableTarget);
        }
        break;
      case HrefType.ANGULAR_ROUTE:
        this.router.navigate([this.clickableHref]);
        this.document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        break;
    }
  }

  async setCaasContent(caasData: CaasComponents['value'][number]) {
    if (!caasData?.formData) {
      console.warn('Prefooter: Caas data or formData is undefined:', caasData);
      return;
    }

    safeAssign(
      () => {
        this.innerHTMLText = this.caasBodytextService.extractTextEditorContent(
          caasData.formData.st_bodytext?.value ?? ''
        );
      },
      'innerHTMLText',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.showBadge = this.setCaasShowBadge();
      },
      'showBadge',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.headline = caasData?.formData?.st_header?.value ?? '';
      },
      'headline',
      this.errorHandler
    );

    safeAssign(
      async () => {
        this.badgeContent = await this.setCaasBadgeContent(caasData);
      },
      'badgeContent',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.imageSrc = caasData?.formData?.st_image?.value?.url ?? '';
      },
      'imageSrc',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.imageTitle = caasData?.formData?.st_image_title?.value ?? '';
      },
      'imageTitle',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.orientation = this.setCaasImagePosition(caasData);
      },
      'orientation',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (caasData?.formData?.st_image?.value?.identifier) {
          this.imageAlt =
            this.imagesService.getAltTextById(
              caasData.formData.st_image.value.identifier
            ) ?? '';
        }
      },
      'imageAlt',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.clickableCategory = this.setCaasLinkTemplate(caasData);
      },
      'clickableCategory',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.clickableText = this.setCaasLinkButtonLabel(caasData);
      },
      'clickableText',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (
          caasData?.formData?.st_link_optional?.value?.formData?.lt_link_label
        ) {
          this.optionalLinkText =
            caasData?.formData?.st_link_optional?.value?.formData?.lt_link_label
              ?.value ?? '';
        }
      },
      'optionalLinkText',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.clickableTitle =
          caasData?.formData?.st_link?.value?.formData?.lt_link_title?.value ??
          '';
      },
      'clickableTitle',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.hrefType = this.caasBodytextService.getHrefType(
          caasData?.formData?.st_link ?? ({} as CaasLink)
        );
      },
      'hrefType',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (caasData?.formData?.st_link?.value?.formData?.lt_button_state) {
          this.isActive =
            caasData?.formData?.st_link?.value?.formData?.lt_button_state?.value;
        }
      },
      'isActive',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.optionalHrefType = this.caasBodytextService.getHrefType(
          caasData?.formData?.st_link_optional ?? ({} as CaasLinkOptional)
        );
      },
      'optionalHrefType',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.clickableType = this.caasBodytextService.getButtonLayout(
          caasData?.formData?.st_link ?? ({} as CaasLink),
          caasData?.formData?.st_link_type ?? undefined
        );
      },
      'clickableType',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.optionalLinkType = this.caasBodytextService.checkOptionalLinksType(
          caasData?.formData?.st_link_optional as CaasLinkOptional
        );
      },
      'optionalLinkType',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.clickableHref = this.caasBodytextService.getHref(
          caasData?.formData?.st_link ?? ({} as CaasLink)
        );
      },
      'clickableHref',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.optionalLinkHref = this.caasBodytextService.getHref(
          caasData?.formData?.st_link_optional ?? ({} as CaasLinkOptional)
        );
      },
      'optionalLinkHref',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.optionalLinkTitle =
          caasData?.formData?.st_link_optional?.value?.formData?.lt_link_title
            ?.value ?? '';
      },
      'optionalLinkTitle',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (
          caasData?.formData?.st_link_optional?.value?.formData
            ?.lt_link_video_type
        ) {
          this.optionalLinkVideo = this.caasBodytextService.getVideo(
            caasData?.formData?.st_link_optional?.value?.formData
              ?.lt_link_video_type,
            caasData?.formData?.st_link_optional?.value?.formData
              ?.lt_link_vimeo_id,
            caasData?.formData?.st_link_optional?.value?.formData
              ?.lt_link_youtube_id
          );
        } else {
          console.warn(
            'Prefooter > st_link_optional:  Video type or ID is missing in :',
            caasData?.formData?.st_link_optional
          );
        }
      },
      'optionalLinkVideo',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (caasData?.formData?.st_link?.value?.formData?.lt_link_video_type) {
          this.clickableVideo = this.caasBodytextService.getVideo(
            caasData?.formData?.st_link?.value?.formData?.lt_link_video_type,
            caasData?.formData?.st_link?.value?.formData?.lt_link_vimeo_id,
            caasData?.formData?.st_link?.value?.formData?.lt_link_youtube_id
          );
        } else {
          console.warn(
            'Prefooter > st_link:  Video type or ID is missing in :',
            caasData?.formData?.st_link
          );
        }
      },
      'clickableVideo',
      this.errorHandler
    );

    safeAssign(
      () => {
        const video = caasData?.formData?.st_image_link
          ? this.setCaasVideo(caasData?.formData?.st_image_link)
          : undefined;

        if (video) {
          this.videoSrc = video.videoSrc;
          this.videoId = video.videoId;
          this.videoTitle =
            caasData?.formData?.st_image_link?.value?.formData?.lt_link_title
              ?.value ?? '';
          this.thumbnailSrc = video.thumbnailSrc;
        }
      },
      'video block',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.badgeIcon = this.setCaasBadgeIcon(
          caasData?.formData?.st_badge_icon
        );
      },
      'badgeIcon',
      this.errorHandler
    );
  }

  async setCaasBadgeContent(
    caasData: CaasComponents['value'][number]
  ): Promise<MediaType> {
    const iconValue = caasData?.formData?.st_badge_icon?.value;
    if (iconValue) {
      const isIconAvailable = this.fontAwesomeService.checkIconAvailable(
        'fal',
        iconValue
      );
      if (isIconAvailable) {
        return MediaType.ICON;
      } else {
        console.warn('Prefooter > Icon: not available:', iconValue);
        return MediaType.ICON;
      }
    } else {
      console.warn(
        'Prefooter > st_badge_icon : No icon value :',
        caasData.formData.st_icon
      );
      return MediaType.IMAGE;
    }
  }

  setCaasImagePosition(
    caasData: CaasComponents['value'][number]
  ): 'forward' | 'reverse' {
    const position = caasData?.formData?.st_image_position?.value?.identifier;
    if (position === 'left') {
      return 'forward';
    } else if (position === 'right') {
      return 'reverse';
    } else {
      console.warn('Prefooter : Invalid or missing image position:', position);
      return 'forward'; // Default fallback
    }
  }

  setCaasLinkTemplate(caasData: CaasComponents['value'][number]): ActionType {
    const buttonLayout =
      caasData?.formData?.st_link?.value?.formData?.lt_button_layout?.value
        ?.identifier;
    if (buttonLayout === 'textlink') {
      return ActionType.LINK;
    } else if (buttonLayout) {
      return ActionType.BUTTON;
    } else {
      console.warn(
        'Prefooter : Missing or invalid button layout:',
        buttonLayout
      );
      return ActionType.NONE; // Default action type
    }
  }

  setCaasLinkButtonLabel(caasData: CaasComponents['value'][number]): string {
    const link = caasData?.formData?.st_link?.value;
    if (link) {
      if (link.template?.uid === 'lt_cta_button') {
        return link.formData?.lt_link_label?.value ?? 'Default Button Label';
      } else {
        return link.formData?.lt_link_title?.value ?? 'Default Title';
      }
    } else {
      console.warn(
        'Prefooter : Missing st_link value:',
        caasData?.formData?.st_link
      );
      return 'Mehr erfahren'; // Default fallback label
    }
  }

  setCaasInnerHTMLText(innerHTMLText: string): string {
    return this.caasBodytextService.extractTextEditorContent(innerHTMLText);
  }

  setCaasVideo(imageLink: CaasImageLink): Video | undefined {
    if (!imageLink.value) return undefined;
    this.showPlayButton = true;
    return this.caasBodytextService.getVideo(
      imageLink?.value?.formData?.lt_link_video_type,
      imageLink?.value?.formData?.lt_link_vimeo_id,
      imageLink?.value?.formData?.lt_link_youtube_id
    );
  }

  setCaasBadgeIcon(badgeIcon: CaasIcon | undefined): string {
    if (!badgeIcon) return '';
    return badgeIcon.value as string;
  }

  setCaasShowBadge(): boolean {
    if (this.caasData?.formData?.st_badge?.value) {
      return true;
    } else {
      return false;
    }
  }
}
